const INBOX = 'argon-share-inbox';

function canUseCache(): boolean {
  return typeof caches !== 'undefined';
}

/** True when the share-target service worker left files in the inbox. */
export async function hasShareInbox(): Promise<boolean> {
  if (!canUseCache()) {
    return false;
  }
  try {
    const cache = await caches.open(INBOX);
    const keys = await cache.keys();
    return keys.length > 0;
  } catch {
    return false;
  }
}

/** Read and delete all stashed share files. */
export async function takeShareInbox(): Promise<File[]> {
  if (!canUseCache()) {
    return [];
  }
  try {
    const cache = await caches.open(INBOX);
    const keys = await cache.keys();
    const files: File[] = [];
    for (const req of keys) {
      const res = await cache.match(req);
      if (!res) {
        continue;
      }
      const blob = await res.blob();
      const encoded = res.headers.get('X-Argon-Filename');
      let name = 'shared';
      if (encoded) {
        try {
          name = decodeURIComponent(encoded);
        } catch {
          name = encoded;
        }
      } else {
        const parts = new URL(req.url).pathname.split('/');
        const last = parts[parts.length - 1];
        if (last) {
          try {
            name = decodeURIComponent(last);
          } catch {
            name = last;
          }
        }
      }
      const type = res.headers.get('Content-Type') || blob.type || 'application/octet-stream';
      files.push(new File([blob], name, { type }));
      await cache.delete(req);
    }
    return files;
  } catch {
    return [];
  }
}

/** Drop stashed share files (cancel / abandon login). */
export async function clearShareInbox(): Promise<void> {
  if (!canUseCache()) {
    return;
  }
  try {
    await caches.delete(INBOX);
  } catch {
    /* ignore */
  }
}

export function registerShareServiceWorker(): void {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
    return;
  }
  navigator.serviceWorker.register('/share-service-worker.js').catch((err) => {
    console.warn('share service worker register failed', err);
  });
}

type LaunchParams = { targetURL?: string };
type LaunchQueue = {
  setConsumer: (cb: (params: LaunchParams) => void) => void;
};
type ArgonLaunchWindow = Window & {
  __argonLaunchParams?: LaunchParams[];
  __argonOnLaunch?: (params: LaunchParams) => void;
  launchQueue?: LaunchQueue;
};

function isShareLaunchUrl(targetURL?: string): boolean {
  if (!targetURL) {
    return false;
  }
  try {
    const url = new URL(targetURL, window.location.origin);
    return url.searchParams.get('share') === '1' || url.pathname === '/share-target';
  } catch {
    return false;
  }
}

/**
 * Share-target reaches the open window via SW postMessage and LaunchQueue.
 */
export function listenForShareLaunch(onShare: () => void): () => void {
  const onMessage = (event: MessageEvent) => {
    if (event.data && event.data.type === 'argon-share-inbox') {
      onShare();
    }
  };
  if (typeof navigator !== 'undefined' && navigator.serviceWorker) {
    navigator.serviceWorker.addEventListener('message', onMessage);
  }
  const w = window as ArgonLaunchWindow;
  const handleLaunch = (params: LaunchParams) => {
    if (isShareLaunchUrl(params.targetURL)) {
      onShare();
    }
  };
  w.__argonOnLaunch = handleLaunch;
  const queued = w.__argonLaunchParams || [];
  w.__argonLaunchParams = [];
  queued.forEach(handleLaunch);
  if (w.launchQueue) {
    w.launchQueue.setConsumer(handleLaunch);
  }
  return () => {
    if (typeof navigator !== 'undefined' && navigator.serviceWorker) {
      navigator.serviceWorker.removeEventListener('message', onMessage);
    }
    if (w.__argonOnLaunch === handleLaunch) {
      w.__argonOnLaunch = undefined;
    }
  };
}
